import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Stack,
  Snackbar,
  Alert,
  IconButton,
  Divider,
  LinearProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Edit, Save, Close, Add, Delete } from "@mui/icons-material";
import { styled } from "@mui/system";
import apiEndpoints from "../../apiconfig";

// Styled Components
const GradientCard = styled(Card)({
  borderRadius: "16px",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  display: "flex",
  flexDirection: "column",
  height: "500px",
  width: "100%",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
  },
  overflow: "auto",
});

const ContentBox = styled(Box)({
  backgroundColor: "rgba(0, 184, 148, 0.05)",
  borderRadius: "10px",
  padding: "12px",
  margin: "8px 0",
  border: "1px solid rgba(0, 184, 148, 0.1)",
  whiteSpace: "pre-wrap",
  overflow: "auto",
  maxHeight: "150px",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    "& fieldset": {
      borderColor: "rgba(0, 184, 148, 0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0, 184, 148, 0.4)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#00b894",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(0, 0, 0, 0.6)",
    "&.Mui-focused": {
      color: "#00b894",
    },
  },
});

function PrivacyPolicy() {
  const [form, setForm] = useState({
    title: "Privacy Policy",
    effectiveDate: "05-06-2025",
    organization: "Devi Chandran Public Welfare Trust",
    website: "www.dcpwtrust.com",
    introduction: "At Devi Chandran Public Welfare Trust, we are committed to protecting your privacy. This policy outlines how we collect, use, store, and protect your personal information.",
    policyItems: [
      {
        id: 1,
        heading: "Information We Collect",
        contents: [
          "We may collect personal details such as name, email, phone number, address, and donation history when you:",
          "Donate through the website",
          "Volunteer with us",
          "Subscribe to updates or newsletters"
        ]
      },
      {
        id: 2,
        heading: "How We Use Your Information",
        contents: [
          "We use your information to:",
          "Process donations and issue receipts",
          "Provide updates about our initiatives",
          "Improve user experience",
          "Comply with legal obligations"
        ]
      },
      {
        id: 3,
        heading: "Data Security",
        contents: [
          "All personal data is securely stored. We use SSL encryption and secure payment gateways (e.g., Razorpay) for all online transactions."
        ]
      },
      {
        id: 4,
        heading: "Sharing of Information",
        contents: [
          "We do not sell or share your data with any third-party marketers. Your data may be accessed by trusted partners solely for operational purposes (e.g., payment processing)."
        ]
      },
      {
        id: 5,
        heading: "Cookies & Analytics",
        contents: [
          "We use cookies and analytics tools (e.g., Google Analytics) to improve website experience and gather usage insights."
        ]
      }
    ],
    email: "Support@dcpwtrust.com",
    address: "Kanchipuram, Tamil Nadu"
  });

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [editItemIndex, setEditItemIndex] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({
    heading: "",
    contents: [""],
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const fetchPolicyData = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiEndpoints.Privacypolicy, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch privacy policy data");
      }

      const data = await response.json();

      if (data) {
        setForm({
          title: data.title || "Privacy Policy",
          effectiveDate: data.effectiveDate || "05-06-2025",
          organization: data.organization || "Devi Chandran Public Welfare Trust",
          website: data.website || "www.dcpwtrust.com",
          introduction: data.introduction || "At Devi Chandran Public Welfare Trust...",
          policyItems: data.policyItems || [
            {
              id: 1,
              heading: "Information We Collect",
              contents: ["We may collect personal details..."]
            }
          ],
          email: data.email || "Support@dcpwtrust.com",
          address: data.address || "Kanchipuram, Tamil Nadu"
        });
      }
    } catch (error) {
      console.error("Error fetching privacy policy:", error);
      setSnackbar({
        open: true,
        message: "Failed to load privacy policy",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePolicyData = async (formData) => {
  setLoading(true);
  try {
    const dataToSend = {
      title: formData.title,
      effectiveDate: formData.effectiveDate,
      organization: formData.organization,
      website: formData.website,
      introduction: formData.introduction,
      policyItems: formData.policyItems, // Send as array, not string
      email: formData.email,
      address: formData.address
    };

    const response = await fetch(apiEndpoints.Privacypolicy, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dataToSend),
    });

    if (!response.ok) {
      throw new Error("Failed to update privacy policy");
    }

    const result = await response.json();
    setSnackbar({
      open: true,
      message: result.message || "Privacy policy updated successfully!",
      severity: "success",
    });
    return result;
  } catch (error) {
    console.error("Error updating privacy policy:", error);
    setSnackbar({
      open: true,
      message: error.message || "Error updating privacy policy",
      severity: "error",
    });
    throw error;
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchPolicyData();
  }, []);

  const handleAddContentField = () => {
    setDialogData({
      ...dialogData,
      contents: [...dialogData.contents, ""],
    });
  };

  const handleContentChange = (index, value) => {
    const newContents = [...dialogData.contents];
    newContents[index] = value;
    setDialogData({
      ...dialogData,
      contents: newContents,
    });
  };

  const handleAddItem = () => {
    if (dialogData.heading.trim() && dialogData.contents.some((c) => c.trim())) {
      const newItem = {
        id: Date.now(),
        heading: dialogData.heading,
        contents: dialogData.contents.filter((c) => c.trim()),
      };
      setForm({ ...form, policyItems: [...form.policyItems, newItem] });
      setDialogData({ heading: "", contents: [""] });
      setDialogOpen(false);
    }
  };

  const handleEditItem = () => {
    if (editItemIndex !== null && dialogData.heading.trim() && dialogData.contents.some((c) => c.trim())) {
      const updatedItems = [...form.policyItems];
      updatedItems[editItemIndex] = {
        ...updatedItems[editItemIndex],
        heading: dialogData.heading,
        contents: dialogData.contents.filter((c) => c.trim()),
      };
      setForm({ ...form, policyItems: updatedItems });
      setEditItemIndex(null);
      setDialogData({ heading: "", contents: [""] });
      setDialogOpen(false);
    }
  };

  const handleDeleteItem = (index) => {
    const updatedItems = [...form.policyItems];
    updatedItems.splice(index, 1);
    setForm({ ...form, policyItems: updatedItems });
  };

  const openEditDialog = (index) => {
    setEditItemIndex(index);
    const item = form.policyItems[index];
    setDialogData({
      heading: item.heading,
      contents: item.contents.length > 0 ? item.contents : [""],
    });
    setDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditItemIndex(null);
    setDialogData({ heading: "", contents: [""] });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updatePolicyData(form);
      setEditing(false);
    } catch {
      setSnackbar({
        open: true,
        message: "Error updating privacy policy.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Box sx={{ width: "100%" }}>
        <Grid container spacing={3} width={'100%'}>
          <Grid item xs={12} width={'100%'}>
            <GradientCard>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  maxWidth: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Privacy Policy Editor
                  </Typography>
                  <IconButton
                    onClick={() => setEditing(!editing)}
                    color="primary"
                    sx={{
                      backgroundColor: editing ? "#ff4444" : "#00b894",
                      color: "white",
                      "&:hover": {
                        backgroundColor: editing ? "#cc0000" : "#009975",
                      },
                    }}
                  >
                    {editing ? <Close /> : <Edit />}
                  </IconButton>
                </Box>

                <Divider sx={{ my: 2 }} />

                <form onSubmit={handleSubmit}>
                  <Stack spacing={2}>
                    {/* Title */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Policy Title
                      </Typography>
                      {editing ? (
                        <StyledTextField
                          name="title"
                          value={form.title}
                          onChange={handleChange}
                          fullWidth
                        />
                      ) : (
                        <ContentBox>
                          <Typography variant="h6">{form.title}</Typography>
                        </ContentBox>
                      )}
                    </Box>

                    {/* Effective Date */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Effective Date
                      </Typography>
                      {editing ? (
                        <StyledTextField
                          name="effectiveDate"
                          value={form.effectiveDate}
                          onChange={handleChange}
                          fullWidth
                        />
                      ) : (
                        <ContentBox>
                          <Typography>Effective Date: {form.effectiveDate}</Typography>
                        </ContentBox>
                      )}
                    </Box>

                    {/* Organization */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Organization
                      </Typography>
                      {editing ? (
                        <StyledTextField
                          name="organization"
                          value={form.organization}
                          onChange={handleChange}
                          fullWidth
                        />
                      ) : (
                        <ContentBox>
                          <Typography>Organization: {form.organization}</Typography>
                        </ContentBox>
                      )}
                    </Box>

                    {/* Website */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Website
                      </Typography>
                      {editing ? (
                        <StyledTextField
                          name="website"
                          value={form.website}
                          onChange={handleChange}
                          fullWidth
                        />
                      ) : (
                        <ContentBox>
                          <Typography>Website: {form.website}</Typography>
                        </ContentBox>
                      )}
                    </Box>

                    {/* Introduction */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Introduction
                      </Typography>
                      {editing ? (
                        <StyledTextField
                          name="introduction"
                          value={form.introduction}
                          onChange={handleChange}
                          fullWidth
                          multiline
                          rows={3}
                        />
                      ) : (
                        <ContentBox>
                          <Typography>{form.introduction}</Typography>
                        </ContentBox>
                      )}
                    </Box>

                    {/* Policy Sections */}
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          Policy Sections
                        </Typography>
                        {editing && (
                          <Button
                            onClick={openAddDialog}
                            startIcon={<Add />}
                            size="small"
                            sx={{
                              backgroundColor: "#00b894",
                              "&:hover": { backgroundColor: "#009975" },
                              borderRadius: "8px",
                              textTransform: "none",
                            }}
                          >
                            Add Section
                          </Button>
                        )}
                      </Box>
                      {editing ? (
                        <Box
                          sx={{
                            backgroundColor: "rgba(0, 184, 148, 0.05)",
                            borderRadius: "10px",
                            padding: "12px",
                            border: "1px solid rgba(0, 184, 148, 0.1)",
                          }}
                        >
                          {form.policyItems.map((item, index) => (
                            <Box
                              key={item.id}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                mb: 2,
                                p: 1,
                                backgroundColor: "rgba(255,255,255,0.7)",
                                borderRadius: "8px",
                              }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{ flexGrow: 1, fontWeight: 600 }}
                                >
                                  {item.heading}
                                </Typography>
                                <IconButton
                                  onClick={() => openEditDialog(index)}
                                  size="small"
                                  sx={{ color: "#00b894" }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleDeleteItem(index)}
                                  size="small"
                                  sx={{ color: "#ff4444" }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                              {item.contents.map((content, i) => (
                                <Typography key={i} sx={{ pl: 2, mt: 1 }}>
                                  • {content}
                                </Typography>
                              ))}
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <ContentBox>
                          {form.policyItems.map((item) => (
                            <Box key={item.id} sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {item.heading}
                              </Typography>
                              {item.contents.map((content, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    mt: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "6px",
                                      height: "6px",
                                      backgroundColor: "#00b894",
                                      borderRadius: "50%",
                                      mt: 1.5,
                                      mr: 1.5,
                                    }}
                                  />
                                  <Typography>{content}</Typography>
                                </Box>
                              ))}
                            </Box>
                          ))}
                        </ContentBox>
                      )}
                    </Box>

                    {/* Email */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Contact Email
                      </Typography>
                      {editing ? (
                        <StyledTextField
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          fullWidth
                          type="email"
                        />
                      ) : (
                        <ContentBox>
                          <Typography>📧 For concerns: {form.email}</Typography>
                        </ContentBox>
                      )}
                    </Box>

                    {/* Address */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Address
                      </Typography>
                      {editing ? (
                        <StyledTextField
                          name="address"
                          value={form.address}
                          onChange={handleChange}
                          fullWidth
                          type="text"
                        />
                      ) : (
                        <ContentBox>
                          <Typography>📍 Address: {form.address}</Typography>
                        </ContentBox>
                      )}
                    </Box>

                    {/* Save Buttons */}
                    {editing && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 2,
                        }}
                      >
                        <Button
                          onClick={() => setEditing(false)}
                          variant="outlined"
                          color="error"
                          startIcon={<Close />}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          sx={{
                            backgroundColor: "#00b894",
                            "&:hover": { backgroundColor: "#009975" },
                          }}
                          startIcon={<Save />}
                        >
                          Save
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </form>
              </CardContent>
              {loading && (
                <LinearProgress
                  sx={{
                    height: 4,
                    "& .MuiLinearProgress-bar": { backgroundColor: "#00b894" },
                  }}
                />
              )}
            </GradientCard>
          </Grid>
        </Grid>
      </Box>

      {/* Dialog with heading and multiple content fields */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editItemIndex !== null
            ? "Edit Policy Section"
            : "Add New Policy Section"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2, height: 300 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Section Heading"
              fullWidth
              value={dialogData.heading}
              onChange={(e) =>
                setDialogData({ ...dialogData, heading: e.target.value })
              }
            />

            {dialogData.contents.map((content, index) => (
              <TextField
                key={index}
                margin="dense"
                label={`Content ${index + 1}`}
                fullWidth
                multiline
                rows={3}
                value={content}
                onChange={(e) => handleContentChange(index, e.target.value)}
              />
            ))}

            <Button
              onClick={handleAddContentField}
              startIcon={<Add />}
              variant="outlined"
              color="primary"
            >
              Add Content Field
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="error">
            Cancel
          </Button>
          <Button
            onClick={editItemIndex !== null ? handleEditItem : handleAddItem}
            disabled={
              !dialogData.heading.trim() ||
              !dialogData.contents.some((c) => c.trim())
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PrivacyPolicy;